import { z } from "zod";
import { containerMetaFieldsSchema, containerMetaStatusSchema, overviewCommentSchema, overviewContainerSchema, overviewSectionSchema, parseArrayItems, parseContainer, parseNullableContainer, toContainer, toMetaContainer, toSectionsContainer, } from "./shared.js";
// Invoice has none of cost-estimate/damage-appraisal's `documents`/`relevantContainers` split, and
// its `item`/`customer` namespaces carry exactly one named container each rather than an open
// namespace — so every schema here is its own fixed shape rather than an extension of a shared base.
// The adapter never emits document data for invoice, so no documentCount field exists.
const metaCommentedContainerSchema = overviewContainerSchema.extend(containerMetaFieldsSchema.shape);
const metaCommentedSectionsSchema = metaCommentedContainerSchema.omit({ data: true }).extend({
    sections: z.array(overviewSectionSchema),
    sectionsHasLoadError: z.boolean(),
});
const invoiceOverviewNamespaceSchema = z.object({
    ContainerIncident: overviewContainerSchema,
    ContainerRepairProcedure: overviewContainerSchema,
    ContainerAutomaticDispatches: overviewContainerSchema,
    ContainerPersonType: overviewContainerSchema,
    ContainerDamage: overviewContainerSchema,
});
const invoiceWorkshopSchema = z.object({
    ContainerEstimate: metaCommentedContainerSchema,
    ContainerRepairService: metaCommentedSectionsSchema,
    ContainerExpertReport: metaCommentedContainerSchema,
    ContainerCommissionedExpertReport: metaCommentedContainerSchema,
    // `null` when neither container names a workshop (confirmed absent).
    ContainerAddressAndContactDetails: overviewContainerSchema.nullable(),
});
const invoiceItemSchema = z.object({
    ContainerItemData: overviewContainerSchema.nullable(),
    ContainerLinkItemIncident: overviewContainerSchema.nullable(),
});
const invoiceCustomerSchema = z.object({
    ContainerPersonComplete: overviewContainerSchema.nullable(),
});
export const invoiceOverviewSchema = z.object({
    claiminiId: z.string().nullish(),
    workflow: z.literal("invoice"),
    overview: invoiceOverviewNamespaceSchema,
    workshop: invoiceWorkshopSchema,
    rentalCar: metaCommentedSectionsSchema,
    towing: metaCommentedSectionsSchema,
    item: invoiceItemSchema,
    customer: invoiceCustomerSchema,
});
const FAILED_META_COMMENTED_CONTAINER = {
    data: null,
    hasLoadError: true,
    metaStatus: null,
    metaHasLoadError: true,
    comments: [],
    commentsHasLoadError: true,
};
const FAILED_META_COMMENTED_SECTIONS = {
    hasLoadError: true,
    metaStatus: null,
    metaHasLoadError: true,
    comments: [],
    commentsHasLoadError: true,
    sections: [],
    sectionsHasLoadError: true,
};
// Invoice's envelope shares no fields with cost-estimate/damage-appraisal's (no
// `documents`/`relevantContainers`), so it's validated as its own loose partitioning rather than
// an extension of a shared base.
export const invoiceEnvelopeSchema = z.object({
    workflow: z.literal("invoice"),
    claiminiId: z.string().nullish(),
    overview: z.record(z.string(), z.unknown()),
    workshop: z.record(z.string(), z.unknown()),
    rentalCar: z.unknown(),
    towing: z.unknown(),
    item: z.record(z.string(), z.unknown()),
    customer: z.record(z.string(), z.unknown()),
});
/** Same strict-first/field-by-field convention as cost-estimate/damage-appraisal's meta-aware-container parsing, minus `documentCount` (invoice never emits it). */
function parseMetaCommentedContainer(raw) {
    const strict = metaCommentedContainerSchema.safeParse(raw);
    if (strict.success)
        return strict.data;
    if (raw == null || typeof raw !== "object")
        return FAILED_META_COMMENTED_CONTAINER;
    const candidate = raw;
    const base = overviewContainerSchema.safeParse({
        data: candidate.data,
        hasLoadError: candidate.hasLoadError,
    });
    const baseData = base.success ? base.data : { data: null, hasLoadError: true };
    const metaStatusParsed = containerMetaStatusSchema.nullable().safeParse(candidate.metaStatus);
    const metaStatus = metaStatusParsed.success ? metaStatusParsed.data : null;
    const metaStatusOk = metaStatusParsed.success;
    const commentsResult = parseArrayItems(overviewCommentSchema, candidate.comments);
    return {
        ...baseData,
        metaStatus,
        metaHasLoadError: candidate.metaHasLoadError === true || !metaStatusOk,
        comments: commentsResult.items,
        commentsHasLoadError: candidate.commentsHasLoadError === true || commentsResult.hasErrors,
    };
}
/** Same strict-first/field-by-field convention as cost-estimate/damage-appraisal's repair-service-container parsing, minus `documentCount`. */
function parseMetaCommentedSections(raw) {
    const strict = metaCommentedSectionsSchema.safeParse(raw);
    if (strict.success)
        return strict.data;
    if (raw == null || typeof raw !== "object")
        return FAILED_META_COMMENTED_SECTIONS;
    const candidate = raw;
    const hasLoadErrorParsed = z.boolean().safeParse(candidate.hasLoadError);
    const hasLoadError = hasLoadErrorParsed.success ? hasLoadErrorParsed.data : true;
    const metaStatusParsed = containerMetaStatusSchema.nullable().safeParse(candidate.metaStatus);
    const metaStatus = metaStatusParsed.success ? metaStatusParsed.data : null;
    const metaStatusOk = metaStatusParsed.success;
    const commentsResult = parseArrayItems(overviewCommentSchema, candidate.comments);
    // Sections is optional at the envelope level (`candidate.sections === undefined` = empty list,
    // not an error), so distinguish "missing" from "invalid": missing → no error, otherwise probe.
    const sectionsResult = candidate.sections === undefined ? { items: [], hasErrors: false } : parseArrayItems(overviewSectionSchema, candidate.sections);
    return {
        hasLoadError,
        metaStatus,
        metaHasLoadError: candidate.metaHasLoadError === true || !metaStatusOk,
        comments: commentsResult.items,
        commentsHasLoadError: candidate.commentsHasLoadError === true || commentsResult.hasErrors,
        sections: sectionsResult.items,
        sectionsHasLoadError: candidate.sectionsHasLoadError === true || sectionsResult.hasErrors,
    };
}
/** Fixed five-container shape (vs. cost-estimate/damage-appraisal's open `overview` namespace) — each key degrades independently. */
function parseInvoiceOverviewNamespace(raw) {
    return {
        ContainerIncident: parseContainer(raw.ContainerIncident),
        ContainerRepairProcedure: parseContainer(raw.ContainerRepairProcedure),
        ContainerAutomaticDispatches: parseContainer(raw.ContainerAutomaticDispatches),
        ContainerPersonType: parseContainer(raw.ContainerPersonType),
        ContainerDamage: parseContainer(raw.ContainerDamage),
    };
}
function parseInvoiceWorkshop(raw) {
    return {
        ContainerEstimate: parseMetaCommentedContainer(raw.ContainerEstimate),
        ContainerRepairService: parseMetaCommentedSections(raw.ContainerRepairService),
        ContainerExpertReport: parseMetaCommentedContainer(raw.ContainerExpertReport),
        ContainerCommissionedExpertReport: parseMetaCommentedContainer(raw.ContainerCommissionedExpertReport),
        ContainerAddressAndContactDetails: parseNullableContainer(raw.ContainerAddressAndContactDetails),
    };
}
// The adapter always emits ContainerItemData (buildReferencedNamespace): `null` = confirmed-absent
// reference (→ MISSING), `{data:null, hasLoadError:true}` = reference-read failure (→ FAILED).
// ContainerLinkItemIncident additionally tolerates the missing key so an adapter that predates it
// degrades to tier `standard`.
function parseInvoiceItem(raw) {
    return {
        ContainerItemData: parseNullableContainer(raw.ContainerItemData),
        ContainerLinkItemIncident: parseNullableContainer(raw.ContainerLinkItemIncident ?? null),
    };
}
// Same referenced-namespace contract as ContainerItemData above.
function parseInvoiceCustomer(raw) {
    return { ContainerPersonComplete: parseNullableContainer(raw.ContainerPersonComplete) };
}
export function parseInvoiceIncidentOverview(envelope) {
    return {
        claiminiId: envelope.claiminiId,
        workflow: "invoice",
        overview: parseInvoiceOverviewNamespace(envelope.overview),
        workshop: parseInvoiceWorkshop(envelope.workshop),
        rentalCar: parseMetaCommentedSections(envelope.rentalCar),
        towing: parseMetaCommentedSections(envelope.towing),
        item: parseInvoiceItem(envelope.item),
        customer: parseInvoiceCustomer(envelope.customer),
    };
}
// ─── Projection to the canonical view ─────────────────────────────────────────────────────────
export function projectInvoiceOverview(overview) {
    const containers = {
        incident: toContainer(overview.overview.ContainerIncident),
        repairProcedure: toContainer(overview.overview.ContainerRepairProcedure),
        automaticDispatches: toContainer(overview.overview.ContainerAutomaticDispatches),
        personType: toContainer(overview.overview.ContainerPersonType),
        damage: toContainer(overview.overview.ContainerDamage),
        estimate: toMetaContainer(overview.workshop.ContainerEstimate),
        expertReport: toMetaContainer(overview.workshop.ContainerExpertReport),
        commissionedExpertReport: toMetaContainer(overview.workshop.ContainerCommissionedExpertReport),
        itemData: overview.item.ContainerItemData == null ? null : toContainer(overview.item.ContainerItemData),
        linkedIncidents: overview.item.ContainerLinkItemIncident == null ? null : toContainer(overview.item.ContainerLinkItemIncident),
        personComplete: overview.customer.ContainerPersonComplete == null ? null : toContainer(overview.customer.ContainerPersonComplete),
        // A load-error container is still projected here so it surfaces as FAILED, not MISSING.
        garageAddress: overview.workshop.ContainerAddressAndContactDetails == null ? null : toContainer(overview.workshop.ContainerAddressAndContactDetails),
    };
    return {
        claiminiId: overview.claiminiId ?? null,
        containers,
        sections: {
            repairRelease: toSectionsContainer(overview.workshop.ContainerRepairService),
            rentalCar: toSectionsContainer(overview.rentalCar),
            towing: toSectionsContainer(overview.towing),
        },
    };
}
